import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { addNewJobTitle } from '../redux/commonSlice';

interface AddJobTitleModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (newJobTitle?: string) => void;
  departmentIds: string;
}

const AddJobTitleModal = ({
  open,
  onClose,
  departmentIds,
  onSuccess,
}: AddJobTitleModalProps) => {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const MAX_LENGTH = 100;

  const payload = {
    roleName: comment,
    departmentId: departmentIds,
  };

  useEffect(() => {
    if (open) {
      setComment('');
    }
  }, [open]);

  const handleSave = async () => {
    if (!comment.trim()) return;
    setIsSubmitting(true);
    try {
      const response = await addNewJobTitle(payload);
      // The response should contain the new job title in the data property
      const newJobTitle = comment; // Use the comment as the new job title
      onClose();
      onSuccess?.(newJobTitle);
      return newJobTitle;
    } catch (error) {
      console.error('Failed to add job title:', error);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Add Job Title
        <IconButton onClick={onClose} size="small" disabled={isSubmitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <TextField
          fullWidth
          placeholder="Job Title"
          multiline
          minRows={3}
          value={comment}
          onChange={e => setComment(e.target.value)}
          disabled={isSubmitting}
          sx={{ mt: 1 }}
          error={comment.length > MAX_LENGTH}
          helperText={
            comment.length > MAX_LENGTH
              ? 'Role Name length must be less than or equal to 100 characters'
              : `${comment.length}/${MAX_LENGTH}`
          }
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            !comment.trim() || comment.length > MAX_LENGTH || isSubmitting
          }
          fullWidth
          size="large"
        >
          {isSubmitting ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddJobTitleModal;
